import React, { useEffect, useRef, useState } from "react";
import { PageHeader, BrutalCard, EmptyState, Chip } from "../components/brutal";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Megaphone, Sparkles, Trash2, Send, Loader2, Download, Share2, Instagram, Quote, Flame, Heart, Dumbbell, Trophy } from "lucide-react";
import { toPng } from "html-to-image";

const REACTIONS = [
  { key: "fire", Icon: Flame, label: "Fire", color: "bg-brand-orange text-white" },
  { key: "clap", Icon: Heart, label: "Clap", color: "bg-brand-pink text-white" },
  { key: "flex", Icon: Dumbbell, label: "Flex", color: "bg-brand-green" },
];

/**
 * ShoutOutInstagramCard — 1080x1080 designed for Instagram feed.
 */
function ShoutOutInstagramCard({ s, forwardedRef }) {
  if (!s) return null;
  const dateLabel = s.created_at ? new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
  return (
    <div
      ref={forwardedRef}
      style={{
        width: 1080,
        height: 1080,
        background: "linear-gradient(135deg, #FFD600 0%, #FF5C00 55%, #FF3AC5 100%)",
        padding: 72,
        display: "flex",
        flexDirection: "column",
        color: "#09090B",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Pattern */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "radial-gradient(circle at 10px 10px, rgba(9,9,11,0.10) 1.2px, transparent 0)",
          backgroundSize: "22px 22px",
        }}
      />

      {/* Brand bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, zIndex: 2 }}>
        <div style={{ width: 72, height: 72, background: "#09090B", borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center", border: "4px solid #09090B", boxShadow: "6px 6px 0 0 #fff" }}>
          <Megaphone color="#fff" size={40} strokeWidth={3} />
        </div>
        <div>
          <div style={{ fontFamily: "'Outfit'", fontWeight: 900, fontSize: 36, lineHeight: 1 }}>ReferPro</div>
          <div style={{ fontSize: 14, letterSpacing: 4, textTransform: "uppercase", fontWeight: 800 }}>Shout-out</div>
        </div>
        <div style={{ marginLeft: "auto", fontSize: 18, fontWeight: 800, background: "#09090B", color: "#fff", padding: "10px 18px", borderRadius: 999, border: "4px solid #09090B" }}>
          {dateLabel}
        </div>
      </div>

      {/* Recipient card */}
      <div
        style={{
          marginTop: 48,
          background: "#fff",
          border: "6px solid #09090B",
          borderRadius: 36,
          padding: 48,
          boxShadow: "12px 12px 0 0 #09090B",
          display: "flex",
          alignItems: "center",
          gap: 36,
          zIndex: 2,
        }}
      >
        <img
          src={s.recipient_avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(s.recipient_name || "?")}`}
          alt=""
          crossOrigin="anonymous"
          style={{ width: 200, height: 200, borderRadius: 32, border: "6px solid #09090B", objectFit: "cover", background: "#fff" }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Outfit'", fontWeight: 900, fontSize: 68, lineHeight: 1, letterSpacing: -1 }}>
            {s.recipient_name}
          </div>
          <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
            {s.recipient_location && (
              <span style={{ fontSize: 22, fontWeight: 800, background: "#FFD600", border: "4px solid #09090B", borderRadius: 999, padding: "8px 20px" }}>
                {s.recipient_location}
              </span>
            )}
            {s.bonus_points > 0 && (
              <span style={{ fontSize: 22, fontWeight: 800, background: "#00E676", border: "4px solid #09090B", borderRadius: 999, padding: "8px 20px" }}>
                +{s.bonus_points} pts
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Message quote */}
      <div
        style={{
          marginTop: 40,
          flex: 1,
          background: "#09090B",
          color: "#fff",
          border: "6px solid #09090B",
          borderRadius: 36,
          padding: 48,
          boxShadow: "12px 12px 0 0 #fff",
          position: "relative",
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Quote color="#FFD600" size={60} strokeWidth={2.5} />
        <div
          style={{
            marginTop: 18,
            fontFamily: "'Outfit'",
            fontWeight: 700,
            fontSize: s.message && s.message.length > 180 ? 38 : 48,
            lineHeight: 1.25,
            letterSpacing: -0.5,
          }}
        >
          {s.message}
        </div>
        <div style={{ marginTop: 28, fontSize: 20, fontWeight: 800, textTransform: "uppercase", letterSpacing: 3, color: "#FFD600" }}>
          — {s.sender_name || "Team"}
        </div>
      </div>

      {/* Footer */}
      <div style={{ marginTop: 24, textAlign: "center", fontSize: 22, fontWeight: 800, color: "#09090B", zIndex: 2 }}>
        #MadeAtReferPro · Refer · Earn · Recognise
      </div>
    </div>
  );
}

function ReactionBar({ s, onReact, disabled }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`reactions-${s.id}`}>
      {REACTIONS.map(({ key, Icon, label, color }) => {
        const count = (s.reactions && s.reactions[key]) || 0;
        const active = s.my_reaction === key;
        return (
          <button
            key={key}
            onClick={() => onReact(s, key)}
            disabled={disabled}
            aria-pressed={active}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border-2 border-black text-xs font-bold transition-transform active:translate-y-0.5 ${
              active ? `${color} shadow-brutal` : "bg-white hover:bg-neutral-100"
            }`}
            title={label}
            data-testid={`react-${key}-${s.id}`}
          >
            <Icon className="w-3.5 h-3.5" strokeWidth={2.6} />
            <span className="tabular-nums">{count}</span>
          </button>
        );
      })}
    </div>
  );
}

function RivalryRecapCard({ s, onDelete, isAdmin }) {
  const recap = s.recap || {};
  const winner = recap.winner || {};
  const runnerUp = recap.runner_up;
  const mvp = recap.mvp;
  const tied = recap.tied;
  const dateLabel = s.created_at ? new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";

  return (
    <BrutalCard
      className="p-5 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #FFD600 0%, #FF5C00 60%, #FF3AC5 100%)" }}
      data-testid={`shoutout-recap-${s.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-black border-2 border-black rounded-2xl flex items-center justify-center shadow-brutal flex-shrink-0">
          <Trophy className="w-6 h-6 text-brand-yellow" strokeWidth={2.6} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Chip color="bg-black text-white">MONTHLY RECAP</Chip>
            <span className="text-[11px] uppercase tracking-widest font-bold text-black/80">
              {recap.month_name} {recap.year} · {dateLabel}
            </span>
          </div>
          <h3 className="font-display font-black text-xl sm:text-2xl mt-2 leading-tight">
            {tied ? `🤝 ${winner.team} & ${runnerUp?.team} tied in ${recap.month_name}` : `🏆 ${winner.team} took the crown in ${recap.month_name}!`}
          </h3>

          <div className="mt-4 grid sm:grid-cols-2 gap-2">
            <div className="brutal bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Winning team</div>
              <div className="font-display font-black text-lg leading-none mt-1">{winner.team}</div>
              <div className="text-sm font-bold mt-1">{winner.total_points} pts · {winner.total_referrals} referrals</div>
            </div>
            {runnerUp ? (
              <div className="brutal bg-white p-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">{tied ? "Other team" : "Runner-up"}</div>
                <div className="font-display font-black text-lg leading-none mt-1">{runnerUp.team}</div>
                <div className="text-sm font-bold mt-1">{runnerUp.total_points} pts · {runnerUp.total_referrals} referrals</div>
              </div>
            ) : null}
          </div>

          {mvp && (
            <div className="brutal bg-black text-white p-3 mt-2 flex items-center gap-3">
              <img
                src={mvp.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(mvp.name)}`}
                alt=""
                crossOrigin="anonymous"
                className="w-10 h-10 rounded-xl border-2 border-white object-cover"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest font-bold text-brand-yellow">MVP</div>
                <div className="font-display font-black truncate">{mvp.name}</div>
              </div>
              <Chip color="bg-brand-yellow text-black">+{mvp.points} pts</Chip>
            </div>
          )}
        </div>
        {isAdmin && (
          <button
            onClick={() => onDelete(s)}
            className="btn-brutal text-xs bg-red-200 border-black self-start"
            data-testid={`shoutout-delete-${s.id}`}
            title="Remove recap"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </BrutalCard>
  );
}

function ShoutOutCard({ s, onDelete, onShare, onDownload, onReact, isAdmin, sharing, isSelf }) {
  return (
    <BrutalCard className="p-5 bg-white" data-testid={`shoutout-${s.id}`}>
      <div className="flex items-start gap-3">
        <img
          src={s.recipient_avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(s.recipient_name || "?")}`}
          alt=""
          crossOrigin="anonymous"
          className="w-14 h-14 rounded-2xl border-2 border-black object-cover bg-white shadow-brutal flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display font-black text-lg">{s.recipient_name}</div>
            {s.recipient_location && <Chip color="bg-neutral-100">{s.recipient_location}</Chip>}
            {s.bonus_points > 0 && (
              <Chip color="bg-brand-green" data-testid={`shoutout-points-${s.id}`}>
                <Sparkles className="w-3 h-3" /> +{s.bonus_points} pts
              </Chip>
            )}
          </div>
          <p className="mt-2 text-sm leading-relaxed">{s.message}</p>
          <div className="mt-3 text-[11px] uppercase tracking-widest font-bold text-neutral-500">
            From {s.sender_name || "Admin"} · {new Date(s.created_at).toLocaleDateString()}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {isSelf ? (
              <div className="text-[11px] text-neutral-500 font-semibold italic">
                Nice — you can't react to your own shout-out.
              </div>
            ) : (
              <ReactionBar s={s} onReact={onReact} />
            )}
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => onShare(s)}
                disabled={sharing}
                className="btn-brutal btn-white text-xs"
                data-testid={`shoutout-share-${s.id}`}
              >
                {sharing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Instagram className="w-3 h-3" />} Share
              </button>
              <button
                onClick={() => onDownload(s)}
                disabled={sharing}
                className="btn-brutal btn-yellow text-xs"
                data-testid={`shoutout-download-${s.id}`}
              >
                {sharing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} PNG
              </button>
            </div>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={() => onDelete(s)}
            className="btn-brutal text-xs bg-red-200 border-black self-start"
            data-testid={`shoutout-delete-${s.id}`}
            title="Remove shout-out"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </BrutalCard>
  );
}

function ReactionLeaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/shoutouts/reaction-leaderboard", { params: { limit: 5 } })
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  }, []);
  if (loading) {
    return (
      <BrutalCard className="p-5">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </BrutalCard>
    );
  }
  return (
    <BrutalCard className="p-5 bg-white" data-testid="reaction-leaderboard">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 bg-brand-pink border-2 border-black rounded-xl flex items-center justify-center">
          <Trophy className="w-4 h-4 text-white" strokeWidth={2.6} />
        </div>
        <h3 className="font-display font-black text-lg">Most loved</h3>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-neutral-500">No reactions yet. Go show someone some love.</div>
      ) : (
        <ol className="space-y-2">
          {rows.map((r) => (
            <li
              key={r.user_id}
              className="flex items-center gap-2 border-2 border-black rounded-xl p-2 bg-white"
              data-testid={`react-lb-${r.rank}`}
            >
              <span className="font-display font-black text-sm w-6 text-center">#{r.rank}</span>
              <img
                src={r.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(r.name)}`}
                alt=""
                className="w-8 h-8 rounded-lg border-2 border-black object-cover bg-white"
              />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{r.name}</div>
                {r.location && <div className="text-[10px] text-neutral-500 uppercase tracking-wider font-bold">{r.location}</div>}
              </div>
              <span className="chip bg-brand-yellow text-[11px] font-black">
                <Flame className="w-3 h-3" /> {r.total_reactions}
              </span>
            </li>
          ))}
        </ol>
      )}
    </BrutalCard>
  );
}

function AdminShoutForm({ onCreated }) {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ recipient_id: "", message: "", bonus_points: 0 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => {
      const emps = r.data.filter((u) => u.role === "employee" && (u.status || "active") === "active");
      setEmployees(emps);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.recipient_id || !form.message.trim()) {
      toast.error("Pick an employee and write a message");
      return;
    }
    setBusy(true);
    try {
      await api.post("/shoutouts", {
        recipient_id: form.recipient_id,
        message: form.message.trim(),
        bonus_points: Number(form.bonus_points) || 0,
      });
      toast.success("Shout-out posted — they'll get a notification");
      setForm({ recipient_id: "", message: "", bonus_points: 0 });
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const presets = [0, 10, 25, 50];

  return (
    <BrutalCard className="p-6 bg-brand-yellow" data-testid="admin-shoutout-form">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 bg-white border-2 border-black rounded-xl flex items-center justify-center shadow-brutal">
          <Megaphone className="w-5 h-5" strokeWidth={2.4} />
        </div>
        <h3 className="font-display font-black text-xl">Give a shout-out</h3>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <select
          className="input-brutal"
          value={form.recipient_id}
          onChange={(e) => setForm({ ...form, recipient_id: e.target.value })}
          data-testid="shoutout-recipient"
        >
          <option value="">Pick someone…</option>
          {employees.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}{(u.team || u.office) ? ` · ${u.team || u.office}` : ""}
            </option>
          ))}
        </select>
        <textarea
          className="input-brutal"
          rows={3}
          maxLength={500}
          placeholder="What did they crush? (max 500 chars)"
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          data-testid="shoutout-message"
        />
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold mb-2">Bonus points</div>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <button
                type="button"
                key={p}
                onClick={() => setForm({ ...form, bonus_points: p })}
                className={`btn-brutal text-xs ${Number(form.bonus_points) === p ? "btn-primary" : "btn-white"}`}
                data-testid={`shoutout-preset-${p}`}
              >
                {p === 0 ? "No points" : `+${p}`}
              </button>
            ))}
            <input
              type="number"
              min={0}
              className="input-brutal text-sm w-24"
              value={form.bonus_points}
              onChange={(e) => setForm({ ...form, bonus_points: e.target.value })}
              data-testid="shoutout-points-input"
            />
          </div>
        </div>
        <button type="submit" disabled={busy} className="btn-brutal btn-primary w-full" data-testid="shoutout-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {busy ? "Posting…" : "Post shout-out"}
        </button>
      </form>
    </BrutalCard>
  );
}

export default function ShoutOutsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null); // id of the card being exported
  const [exportTarget, setExportTarget] = useState(null); // full shoutout object to render offscreen
  const exportRef = useRef(null);

  const load = () => api.get("/shoutouts").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const onReact = async (s, type) => {
    try {
      const { data } = await api.post(`/shoutouts/${s.id}/reactions`, { type });
      setItems((prev) => prev.map((it) => (it.id === s.id ? { ...it, ...data } : it)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDelete = async (s) => {
    if (!window.confirm(`Remove the shout-out for ${s.recipient_name}? Any bonus points awarded will be reversed.`)) return;
    try {
      await api.delete(`/shoutouts/${s.id}`);
      toast.success("Shout-out removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const renderAndExport = async (s) => {
    // Render the offscreen card, wait a tick for layout + images, then toPng
    setExportTarget(s);
    // Wait for React commit + avatar load
    await new Promise((r) => setTimeout(r, 350));
    const node = exportRef.current;
    if (!node) return null;
    const dataUrl = await toPng(node, {
      cacheBust: true,
      pixelRatio: 1,
      width: 1080,
      height: 1080,
    });
    return dataUrl;
  };

  const onDownload = async (s) => {
    setExporting(s.id);
    try {
      const dataUrl = await renderAndExport(s);
      if (!dataUrl) throw new Error("no image");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `shoutout-${(s.recipient_name || "referpro").replace(/\s+/g, "-").toLowerCase()}.png`;
      a.click();
      toast.success("Image downloaded — share it on Instagram!");
    } catch (err) {
      toast.error("Couldn't export image. Try again.");
    } finally {
      setExporting(null);
      setExportTarget(null);
    }
  };

  const onShare = async (s) => {
    setExporting(s.id);
    try {
      const dataUrl = await renderAndExport(s);
      if (!dataUrl) throw new Error("no image");
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `shoutout-${s.id}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Shout-out for ${s.recipient_name}`,
          text: s.message + " #MadeAtReferPro",
        });
      } else {
        // Fallback: trigger download
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `shoutout-${(s.recipient_name || "referpro").replace(/\s+/g, "-").toLowerCase()}.png`;
        a.click();
        toast.success("Image downloaded — share it on Instagram!");
      }
    } catch (err) {
      toast.error("Couldn't share image. Try again.");
    } finally {
      setExporting(null);
      setExportTarget(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Shout-outs"
        subtitle="Public recognition from the team. When someone crushes it, it lives here."
      />

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          {loading ? (
            <div className="text-center p-10"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <EmptyState
              title="No shout-outs yet"
              description={isAdmin ? "Be the first — pick a star performer and give them public props." : "Once leaders recognize someone, their shout-out shows up here."}
            />
          ) : (
            items.map((s) => (
              s.kind === "rivalry_recap" ? (
                <RivalryRecapCard
                  key={s.id}
                  s={s}
                  onDelete={onDelete}
                  isAdmin={isAdmin}
                />
              ) : (
                <ShoutOutCard
                  key={s.id}
                  s={s}
                  onDelete={onDelete}
                  onShare={onShare}
                  onDownload={onDownload}
                  onReact={onReact}
                  isAdmin={isAdmin}
                  sharing={exporting === s.id}
                  isSelf={s.recipient_id === user?.id}
                />
              )
            ))
          )}
        </div>
        <div className="lg:col-span-1 space-y-4">
          {isAdmin && <AdminShoutForm onCreated={load} />}
          <ReactionLeaderboard />
        </div>
      </div>

      {/* Offscreen export surface — rendered at 1080x1080 and positioned outside viewport */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: -99999,
          top: 0,
          width: 1080,
          height: 1080,
          pointerEvents: "none",
        }}
      >
        <ShoutOutInstagramCard s={exportTarget} forwardedRef={exportRef} />
      </div>
    </div>
  );
}
